import React from 'react';
import {useTranslation} from 'react-i18next';
import {useOrganization} from "@/hooks/useOrganization.js";
import {FIXTURE_STATE_CONFIG} from "@/utils/constants.js";

export const ContestHeader = ({contestData, slug, recentGoal}) => {

    const {t} = useTranslation();
    const {organization} = useOrganization(contestData ? null : slug);

    const coverUrl = contestData?.coverUrl || organization?.coverUrl;

    // Lot mi-temps : affiché en teaser tant qu'il est encore gagnable (avant la pause).
    const halfTimeRewardLabel = contestData?.participantUi?.halfTimeRewardLabel;
    const beforeHalfTime = [
        FIXTURE_STATE_CONFIG.NOT_STARTED,
        FIXTURE_STATE_CONFIG.FIRST_HALF
    ].includes(contestData?.fixture?.state);
    const showHalfTimeTeaser = !!halfTimeRewardLabel && beforeHalfTime;

    // 1. On force la classe "shadow-none" si y a un but pour être sûr à 100% que Tailwind nettoie l'ombre
    let containerClasses = contestData
        ? "bg-brand-dark text-white relative rounded-b-[2rem] overflow-hidden pb-12 transition-shadow duration-300"
        : "bg-brand-dark text-white relative rounded-b-[2rem] overflow-hidden transition-shadow duration-300";

    if (recentGoal) {
        containerClasses += " shadow-none"; // FORCE 0 OMBRE
    } else {
        containerClasses += contestData
            ? " shadow-2xl shadow-black/40"
            : " shadow-lg shadow-black/40";
    }

    const imgClasses = contestData
        ? "w-full h-55 object-cover opacity-80 clip-image"
        : "w-full h-55 object-cover opacity-80 animate-fade-in clip-image";

    return (
        <div className={containerClasses}>

            {coverUrl && (
                <img
                    src={coverUrl}
                    alt="Header"
                    className={imgClasses}
                />
            )}

            {recentGoal && (
                <div
                    className="absolute inset-0 z-10 bg-white rounded-b-[2rem] flex flex-col items-center justify-center overflow-hidden">
                    <style>
                        {`
                            @keyframes flashRed {
                                0% { opacity: 1; }
                                100% { opacity: 0; }
                            }
                            @keyframes wipeCurtain {
                                0% { transform: translateX(120%) skewX(-12deg); }
                                100% { transform: translateX(-10%) skewX(-12deg); }
                            }
                            @keyframes wipeText {
                                0% { transform: translateX(-150%); opacity: 0; }
                                100% { transform: translateX(0); opacity: 1; }
                            }
                            @keyframes wipeSub {
                                0% { transform: translateX(150%); opacity: 0; }
                                100% { transform: translateX(0); opacity: 1; }
                            }
                            
                            /* 💡 NOUVEAU : L'animation continue (Battement de cœur / Respiration) */
                            @keyframes pulseText {
                                0%, 100% { transform: translateX(0) scale(1); }
                                50% { transform: translateX(0) scale(1.08); text-shadow: 0 0 20px rgba(255,255,255,0.4); }
                            }
                            
                            .anim-flash { animation: flashRed 0.4s ease-out forwards; }
                            .anim-curtain { animation: wipeCurtain 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.15s forwards; transform: translateX(120%) skewX(-12deg); }
                            
                            /* 💡 MODIFIÉ : On chaîne "wipeText" (l'entrée) avec "pulseText" (la boucle infinie) */
                            .anim-text { 
                                animation: 
                                    wipeText 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.35s forwards,
                                    pulseText 1.2s ease-in-out 0.85s infinite; /* Se lance pile quand l'entrée se termine */
                                transform: translateX(-150%); opacity: 0; 
                            }
                            
                            .anim-sub { animation: wipeSub 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.5s forwards; transform: translateX(150%); opacity: 0; }
                        `}
                    </style>

                    {/* Flash d'impact (Rouge sur fond blanc) */}
                    <div className="absolute inset-0 bg-[#FF003C] z-40 anim-flash pointer-events-none"></div>

                    {/* LE RIDEAU ROUGE : Aplât pur */}
                    <div className="absolute inset-0 w-[120%] bg-[#FF003C] z-10 anim-curtain"></div>

                    {/* Conteneur des textes */}
                    <div className="relative z-20 flex flex-col items-center justify-center w-full">

                        {/* TEXTE BLANC (Il va maintenant palpiter une fois arrivé !) */}
                        <span
                            className="anim-text font-black italic uppercase tracking-tighter text-white text-[4rem] sm:text-[5rem] md:text-[6rem] leading-none">
                            {t('overlay_goal.title', 'BUUUUT !')}
                        </span>

                        {/* CARTOUCHE BLANCHE */}
                        {typeof recentGoal === 'object' && recentGoal.teamName && (
                            <div
                                className="anim-sub mt-2 bg-white text-[#FF003C] px-8 py-1.5 font-black text-xl md:text-2xl uppercase tracking-widest transform -skew-x-12">
                                <span className="transform skew-x-12 block">
                                    {recentGoal.teamName}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* --- TEXTE NORMAL DU HEADER (Masqué si but) --- */}
            {!recentGoal && (
                <div className="absolute bottom-12 left-0 right-0 text-center px-6">
                    {contestData ? (
                        <>
                            <h1 className="text-3xl font-black leading-none mb-1">
                                <span className="italic text-yellow-400">
                                    {contestData?.participantUi?.rewardLabel}
                                </span> {t('header.to_win')}
                            </h1>
                            {showHalfTimeTeaser && (
                                <p className="text-sm font-bold text-indigo-300 mb-1.5 flex items-center justify-center gap-1.5">
                                    <span>⏱️</span>
                                    <span>
                                        <span className="text-yellow-400">+{halfTimeRewardLabel}</span>{' '}
                                        {t('header.half_time_suffix', 'dès la mi-temps')}
                                    </span>
                                </p>
                            )}
                            <p className="text-xs text-gray-300 font-medium opacity-80">
                                {t('header.subtitle')}
                            </p>
                        </>
                    ) : (
                        <div/>
                    )}
                </div>
            )}

        </div>
    );
};